import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  InputField,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  getSidePanelChildForRoles,
  getSidePanelEntitlementForRoles,
} from '../../../../../redux/certdetails/action';
import { SIDE_PANEL_LOAD_MORE_BUTTON } from '../../../../../redux/certdetails/types';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  REF_PROP,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../../../utils/constant';
import { useInvertedLocalize } from '../../../../../utils/localization';
import {
  getMessageKey,
  getRiskMeterParamsForConfidence,
  getStateForPAA,
} from '../../../../../utils/utils';
import { TrustJustification } from '../../../../CertsDetailsList';
import RiskScore from '../../../../RiskScore';
import { invertedMessages } from '../../messages';
import { FieldSet, HLine, SimpleAccordion } from '../../SidePanelCommon';
import {
  CertsSidePanelBodyError,
  CertsSidePanelBodyHistory,
  CertsSidePanelBodySummary,
} from '../CertsSidePanelBodyCommon';
import {
  DEFAULT_COUNT_ACCORDIANS_OPEN,
  MAX_RECORDS_TO_FETCH,
  TAB_CHILDREN,
  TAB_ENTITLEMENT,
  TAB_HISTORY,
  TAB_SUMMARY,
} from '../constant';
import DATA from '../mockData';

import './CertsSidePanelRoles.css';

function CertsSidePanelRoles({
  dataTestId,
  className,
  data,
  activeTab,
  setActiveTab,
  isLoadingHistory,
  retriggerApisForError,
  handleCloseSidePanel,
  tabGroupSidePanelRef,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);
  const {
    core: { configMap, isTrustScoreEnabled, campaignType },
    global: { mediaQueryValue },
  } = useSelector((state) => state?.certifications?.details);

  const classes = classnames('CertsSidePanelBody', className);

  const l = useInvertedLocalize(invertedMessages);

  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [viewMore, setViewMore] = useState({
    [TAB_ENTITLEMENT]: { currentOffset: 0 },
    [TAB_CHILDREN]: { currentOffset: 0 },
  });
  const [entitlementSearchForRoles, setEntitlementSearchForRoles] =
    useState('');
  const [childrenSearchForRoles, setChildrenSearchForRoles] = useState('');

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const callApiForLoadBtn = (tab) => {
    setIsLoadingMoreData(true);

    switch (tab) {
      case TAB_ENTITLEMENT:
        dispatch(
          getSidePanelEntitlementForRoles(
            data.CERT_ROLEKEY,
            String(
              viewMore[TAB_ENTITLEMENT].currentOffset + MAX_RECORDS_TO_FETCH
            ),
            String(MAX_RECORDS_TO_FETCH),
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });
        break;

      case TAB_CHILDREN:
        dispatch(
          getSidePanelChildForRoles(
            data.CERT_ROLEKEY,
            String(viewMore[TAB_CHILDREN].currentOffset + MAX_RECORDS_TO_FETCH),
            String(MAX_RECORDS_TO_FETCH),
            SIDE_PANEL_LOAD_MORE_BUTTON
          )
        )
          .then(() => {
            setViewMore((prevViewMore) => ({
              ...prevViewMore,
              [tab]: {
                currentOffset:
                  prevViewMore[tab].currentOffset + MAX_RECORDS_TO_FETCH,
              },
            }));
            setIsLoadingMoreData(false);
          })
          .catch(() => {
            setIsLoadingMoreData(false);
          });
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (data) {
      if (data?.sidePanel?.status[TAB_SUMMARY]) setIsLoadingData(false);
    }
  }, [data]);

  useEffect(() => {
    setEntitlementSearchForRoles('');
    setChildrenSearchForRoles('');
    setShowHistory(activeTab === TAB_HISTORY);
  }, [activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const renderHistoryContent = () => {
    if (showHistory) {
      if (data?.sidePanel?.status[TAB_HISTORY]) {
        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--history'
            value={activeTab}
            tabValue={TAB_HISTORY}>
            <CertsSidePanelBodyHistory
              data={data}
              isLoadingHistory={isLoadingHistory}
              isMobileWidth={isMobileWidth}
            />
          </TabContent>
        );
      }

      return (
        <CertsSidePanelBodyError
          tab={TAB_HISTORY}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent--history'
        value={activeTab}
        tabValue={TAB_HISTORY}>
        <div />
      </TabContent>
    );
  };

  const renderEntitlementTab = () => {
    if (data) {
      return (
        <TabItem
          label={l('Entitlement')}
          key={TAB_ENTITLEMENT}
          value={TAB_ENTITLEMENT}
          icon={<Icon kind='Entitlement' size='medium' color='neutral-1000' />}
          size='medium'
        />
      );
    }

    return (
      <span
        className='CertsSidePanelBody-tabGroupEmpty'
        key={TAB_ENTITLEMENT}
        value={TAB_ENTITLEMENT}
      />
    );
  };

  const renderChildrenTabForRoles = () => {
    if (data) {
      if (data?.sidePanel?.status[TAB_CHILDREN]) {
        if (data?.sidePanel?.children?.totalChildRole > 0) {
          return (
            <TabItem
              label={l('Children')}
              key={TAB_CHILDREN}
              value={TAB_CHILDREN}
              icon={
                <Icon kind='Entitlement' size='medium' color='neutral-1000' />
              }
              size='medium'
            />
          );
        }

        return <span className='CertsSidePanelBody-tabGroupEmpty' />;
      }

      return (
        <TabItem
          label={l('Children')}
          value={TAB_CHILDREN}
          icon={<Icon kind='Entitlement' size='medium' color='neutral-1000' />}
          size='medium'
        />
      );
    }

    return <span className='CertsSidePanelBody-tabGroupEmpty' />;
  };

  const renderEntitlementContentForRoles = () => {
    if (data?.sidePanel?.status[TAB_ENTITLEMENT]) {
      if (data?.sidePanel?.entitlements?.totalEnt > 0) {
        const messageKeys = data?.sidePanel?.entitlements?.messageKeys;

        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--entitlement'
            value={activeTab}
            tabValue={TAB_ENTITLEMENT}>
            <div className='CertsSidePanelBody-tabContentEntitlementSearch'>
              <InputField
                name='basic'
                kind='search'
                placeholder={l('Search by Entitlement Name')}
                prefixIcon={
                  <Icon kind='search' color='neutral-600' size='smallMedium' />
                }
                value={entitlementSearchForRoles}
                setValue={setEntitlementSearchForRoles}
              />
            </div>
            {data?.sidePanel?.entitlements?.roleEntList
              .filter(
                (child) =>
                  `${child.entitlement_value}`
                    .toLowerCase()
                    .indexOf(entitlementSearchForRoles.toLowerCase()) !== -1
              )
              .map((child, index) => (
                <SimpleAccordion
                  key={child.index}
                  title={child.entitlement_value}
                  iconKind='Entitlement'
                  isInitialOpen={index < DEFAULT_COUNT_ACCORDIANS_OPEN}>
                  {Object.keys(child)
                    .filter(
                      (node) => node !== 'entitlement_value' && node !== 'index'
                    )
                    .map((node) => (
                      <FieldSet
                        key={`${child.index}-${node}`}
                        fieldData={{
                          label:
                            messages[messageKeys[node]] ||
                            localize(intl, getMessageKey(node)),
                          value: child[node],
                        }}
                      />
                    ))}
                  {index !==
                    data.sidePanel.entitlements.roleEntList.length - 1 && (
                    <HLine />
                  )}
                </SimpleAccordion>
              ))}
            {data?.sidePanel?.entitlements?.totalEnt > MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_ENTITLEMENT].currentOffset +
                      MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.entitlements?.totalEnt ||
                    isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_ENTITLEMENT)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_ENTITLEMENT}>
        <CertsSidePanelBodyError
          tab={TAB_ENTITLEMENT}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  const renderChildrenContentForRoles = () => {
    if (data?.sidePanel?.status[TAB_CHILDREN]) {
      if (data?.sidePanel?.children?.totalChildRole > 0) {
        const messageKeys = data?.sidePanel?.children?.messageKeys;

        return (
          <TabContent
            className='CertsSidePanelBody-tabContent--child'
            value={activeTab}
            tabValue={TAB_CHILDREN}>
            {/* TODO: This is temp solution */}
            <Typography
              kind='body3'
              className='CertsSidePanelBody-tabContentChildDescriptionGap'>
              {`${'Text for gap'}`}
            </Typography>
            <InputField
              name='basic'
              kind='search'
              placeholder={l('Search by Entitlement Name')}
              prefixIcon={
                <Icon kind='search' color='neutral-600' size='smallMedium' />
              }
              value={childrenSearchForRoles}
              setValue={setChildrenSearchForRoles}
            />
            {data?.sidePanel?.children?.childRoleList
              .filter(
                (child) =>
                  `$${child.displayname}`
                    .toLowerCase()
                    .indexOf(childrenSearchForRoles.toLowerCase()) !== -1
              )
              .map((child, index) => (
                <SimpleAccordion
                  key={child.index}
                  title={child.displayname || child.role_name || ''}
                  iconKind='Entitlement'
                  isInitialOpen={index < DEFAULT_COUNT_ACCORDIANS_OPEN}>
                  {Object.keys(child)
                    .filter(
                      (node) => node !== 'displayname' && node !== 'index'
                    )
                    .map((node) => (
                      <FieldSet
                        key={`${child.index}-${node}`}
                        fieldData={{
                          label:
                            messages[messageKeys[node]] ||
                            localize(intl, getMessageKey(node)),
                          value: child[node],
                        }}
                      />
                    ))}
                  {index !==
                    data.sidePanel.children.childRoleList.length - 1 && (
                    <HLine />
                  )}
                </SimpleAccordion>
              ))}
            {data?.sidePanel?.children?.totalChildRole >
              MAX_RECORDS_TO_FETCH && (
              <div className='CertsSidePanelBody-tabContentLoadBtn'>
                <Button
                  type='button'
                  kind='subtle'
                  size='medium'
                  isDisabled={
                    viewMore[TAB_CHILDREN].currentOffset +
                      MAX_RECORDS_TO_FETCH >
                      data?.sidePanel?.children?.totalChildRole ||
                    isLoadingMoreData
                  }
                  onClick={() => callApiForLoadBtn(TAB_CHILDREN)}>
                  {l('Load More')}
                </Button>
              </div>
            )}
          </TabContent>
        );
      }

      return <span />;
    }

    return (
      <TabContent
        className='CertsSidePanelBody-tabContent'
        value={activeTab}
        tabValue={TAB_CHILDREN}>
        <CertsSidePanelBodyError
          tab={TAB_CHILDREN}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      </TabContent>
    );
  };

  const shouldShowSidePanelSummaryForRoles = Boolean(
    data?.sidePanel?.summary && data?.sidePanel?.status[TAB_SUMMARY]
  );

  const renderScoreMeter = () => {
    if (isTrustScoreEnabled) {
      // Check if score coming from confidence
      if (isTrustScoreEnabled?.enableCertificationTrustModel) {
        const upperLimitValue =
          configMap?.TRUST_LIKELYAPPROVAL_LOWER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_LIKELYAPPROVAL_LOWER)
            : null;
        const lowerLimitValue =
          configMap?.TRUST_POSSIBLEREJECT_UPPER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_POSSIBLEREJECT_UPPER)
            : null;
        const middleRange = String(configMap?.TRUST_POSSIBLEAPPROVAL);

        const {
          score: riskScore,
          kind: riskKind,
          label: riskLabel,
        } = getRiskMeterParamsForConfidence(
          Number(data?.TRUST_SCORE) || Number(data?.JRMRULES?.TRUST_SCORE),
          upperLimitValue,
          lowerLimitValue,
          middleRange
        );

        if (riskKind === 'info') return null;

        return (
          <>
            <div className='CertsSidePanelBody-tabContentSummaryShorts'>
              <div className='.CertsSidePanelBody-tabContentSummaryShortsStatus'>
                <Typography kind='h4'>{l('Trust')}</Typography>
              </div>
              <div
                className={
                  isMobileWidth
                    ? 'CertsSidePanelBody-tabContentSummaryShortsRisk--mobileResponsive'
                    : 'CertsSidePanelBody-tabContentSummaryShortsRisk'
                }>
                <div className='CertsSidePanelBody-tabContentSummaryShortsRiskScore'>
                  <RiskScore
                    kind={riskKind}
                    size={isMobileWidth ? 'small' : 'regular'}
                    value={riskScore}
                    label={riskLabel}
                    shouldShowZero
                  />
                </div>
                <div className='CertsSidePanelBody-tabContentSummaryShortsRiskList'>
                  <TrustJustification
                    isTrustScoreEnabled={isTrustScoreEnabled}
                    nodeData={data}
                    certsKind='Role'
                  />
                </div>
              </div>
            </div>
            <HLine />
          </>
        );
      }

      const riskScore = Number(data?.JRMRULES?.cnf || '0');
      const { kind: riskKind, label: riskLabel } = getStateForPAA(
        data?.JRMRULES?.rmd
      );

      if (riskKind === 'info' || Number(riskScore) === 0) return null;

      return (
        <>
          <div className='CertsSidePanelBody-tabContentSummaryShorts'>
            <div className='.CertsSidePanelBody-tabContentSummaryShortsStatus'>
              <Typography kind='h4'>{l('Trust')}</Typography>
            </div>
            <div
              className={
                isMobileWidth
                  ? 'CertsSidePanelBody-tabContentSummaryShortsRisk--mobileResponsive'
                  : 'CertsSidePanelBody-tabContentSummaryShortsRisk'
              }>
              <div className='CertsSidePanelBody-tabContentSummaryShortsRiskScore'>
                <RiskScore
                  kind={riskKind}
                  size={isMobileWidth ? 'small' : 'regular'}
                  value={riskScore}
                  label={riskLabel}
                />
              </div>
              <div className='CertsSidePanelBody-tabContentSummaryShortsRiskList'>
                <TrustJustification
                  isTrustScoreEnabled={isTrustScoreEnabled}
                  nodeData={data}
                  certsKind='Role'
                />
              </div>
            </div>
          </div>
          <HLine />
        </>
      );
    }

    return null;
  };

  /**
   * Renders summary tab content based on data availability and loading state.
   * Returns CertsSidePanelBodySummary when data is available,
   * returns CertsSidePanelBodyError component when data failed to load,
   * and returns loading indicator while fetching data.
   */
  const renderSummaryContent = () => {
    if (!isLoadingData && shouldShowSidePanelSummaryForRoles) {
      return <CertsSidePanelBodySummary data={data} />;
    }

    if (
      !isLoadingData &&
      !shouldShowSidePanelSummaryForRoles &&
      !data?.sidePanel?.status[TAB_SUMMARY]
    ) {
      return (
        <CertsSidePanelBodyError
          tab={TAB_SUMMARY}
          retriggerApisForError={retriggerApisForError}
          handleCloseSidePanel={handleCloseSidePanel}
        />
      );
    }

    // Default to loading UI for all other cases
    return (
      <Box className='SidePanelView-loader'>
        <Loader kind='dots' format='inline' color='info' size='medium' />
      </Box>
    );
  };

  return (
    <section className={classes} data-testid={dataTestId}>
      <TabGroup
        moreContainerRef={tabGroupSidePanelRef}
        value={activeTab}
        isVerticalWithOverflow={isMobileWidth}
        onChange={handleTabChange}
        isTabOrderBeFixed
        className='CertsSidePanelBody-tabGroup'>
        <TabItem
          label={l('Summary')}
          key={TAB_SUMMARY}
          value={TAB_SUMMARY}
          icon={<Icon kind='Document' size='medium' color='neutral-1000' />}
          size='medium'
        />
        <TabItem
          label={l('History')}
          key={TAB_HISTORY}
          value={TAB_HISTORY}
          icon={
            <Icon kind='RequestHistory' size='medium' color='neutral-1000' />
          }
          size='medium'
        />

        {renderEntitlementTab()}
        {renderChildrenTabForRoles()}
      </TabGroup>

      <TabContent
        className='CertsSidePanelBody-tabContent--summary'
        value={activeTab}
        tabValue={TAB_SUMMARY}>
        {isTrustScoreEnabled &&
          shouldShowSidePanelSummaryForRoles &&
          !isSACampaign &&
          renderScoreMeter()}
        {renderSummaryContent()}
      </TabContent>

      {renderHistoryContent()}
      {renderEntitlementContentForRoles()}
      {renderChildrenContentForRoles()}
    </section>
  );
}

CertsSidePanelRoles.propTypes = {
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  data: PropTypes.shape(DATA).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  retriggerApisForError: PropTypes.func,
  handleCloseSidePanel: PropTypes.func,
  tabGroupSidePanelRef: REF_PROP.isRequired,
};

CertsSidePanelRoles.defaultProps = {
  dataTestId: null,
  className: null,
  retriggerApisForError: () => {},
  handleCloseSidePanel: () => {},
};

export default CertsSidePanelRoles;
